import React, { createContext, useContext, useState, useEffect } from "react";
import axiosClient from "../api/axiosClient";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { ALL_SIDEBAR_ITEMS } from "../constants/sidebarItems";
import {
  FIELD_VENDOR_SECTIONS,
  FIELD_VENDOR_EXCLUSIVE_SECTIONS,
  isFieldVendorLevel,
} from "../utils/homePath";

const AccessContext = createContext();

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return context;
};

export const AccessProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [accessConfig, setAccessConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [userLevel, setUserLevel] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = jwtDecode(token);
        const level = Number(
          payload?.level ?? payload?.dept_level ?? payload?.admin_level ?? 99
        );
        console.log("[AccessProvider] Token decoded, userLevel:", level, "user_id:", payload?.user_id);
        setUserLevel(level);
        setIsAdmin(
          payload?.user_id === "admin" ||
            payload?.role === "admin" ||
            payload?.type === "admin_session" ||
            level === 0
        );
      } catch (err) {
        console.error("Error decoding token in AccessProvider", err);
      }
    }
    fetchAccessConfig();
  }, []);

  const fetchAccessConfig = async () => {
    const timeoutMs = 5000;
    try {
      setLoading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const data = await axiosClient.get("/ventas/settings/access", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      // Si data es un array o algo raro por el interceptor, manejamos
      console.log("[AccessProvider] Fetched access config:", data);
      setAccessConfig(data || {});

      // Aplicar lenguaje guardado si existe
      if (data?.language && i18n.language !== data.language) {
        console.log(`[AccessProvider] Setting language to ${data.language}`);
        i18n.changeLanguage(data.language);
      }
    } catch (error) {
      console.error("Error fetching access config:", error);
      // En caso de fallo, accessConfig queda {} y usamos defaultLevel
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      await axiosClient.post("/ventas/settings/access", newConfig);
      setAccessConfig(newConfig);
      return { success: true };
    } catch (error) {
      console.error("Error updating access config:", error);
      return { success: false, error };
    }
  };

  const getRequiredLevel = (sectionId) => {
    // Si hay config en DB, la usamos. Si no, usamos el default de la constante.
    if (accessConfig[sectionId] !== undefined) {
      return Number(accessConfig[sectionId]);
    }
    const item = ALL_SIDEBAR_ITEMS.find((it) => it.id === sectionId);
    return item ? item.defaultLevel : 0; // Por defecto admin si no se encuentra
  };

  const canAccess = (sectionId) => {
    // Rol "vendedor de campo" (rango acotado, ver `isFieldVendorLevel`): acceso
    // EXPLÍCITO y EXACTO a FIELD_VENDOR_SECTIONS, ignorando accessConfig
    // (overrides de BD) y defaultLevel por completo. Sin esto, un override real
    // de producción (supervision_tablets=3) le negaría al rol una de sus dos
    // únicas secciones porque `4 <= 3` es falso (Hallazgo B, tarea 7).
    // `isFieldVendorLevel` (rango acotado, NO "mayor o igual") es lo que evita
    // que un nivel desconocido (99, fallback de JWT ilegible) gane este rol —
    // con el `>=` de antes, un admin o vendedor sin `level` legible caía aquí,
    // ANTES del atajo `isAdmin`, y perdía el CRM entero (Hallazgo 1, tarea 7).
    if (isFieldVendorLevel(userLevel)) {
      const hasAccess = FIELD_VENDOR_SECTIONS.includes(sectionId);
      console.log(`[AccessContext] canAccess(${sectionId}): vendedor de campo (userLevel=${userLevel}), hasAccess=${hasAccess}`);
      return hasAccess;
    }

    // Secciones exclusivas del rol nuevo (hoy solo "crear_acceso"): ningún otro
    // nivel las ve, NI SIQUIERA el admin. Esta comprobación va ANTES del atajo
    // `isAdmin` de abajo a propósito: `crear_acceso` tiene defaultLevel:4, que
    // por la semántica `<=` significa "visible para TODOS" (incluido el admin),
    // justo la confusión que le hacía aparecer a Trini y a todo vendedor
    // (Hallazgo A, tarea 7).
    if (FIELD_VENDOR_EXCLUSIVE_SECTIONS.includes(sectionId)) {
      console.log(`[AccessContext] canAccess(${sectionId}): sección exclusiva del vendedor de campo, denegado (userLevel=${userLevel}, isAdmin=${isAdmin})`);
      return false;
    }

    if (isAdmin) return true;
    if (userLevel == null) return false;
    const requiredLevel = getRequiredLevel(sectionId);
    const hasAccess = userLevel <= requiredLevel;
    console.log(`[AccessContext] canAccess(${sectionId}): userLevel=${userLevel}, requiredLevel=${requiredLevel}, hasAccess=${hasAccess}`);
    // userLevel 0 es el más alto. 3 es el más bajo.
    // Si userLevel <= requiredLevel, tiene acceso.
    // Ej: requiredLevel 0 (Admin), userLevel 0 -> OK. userLevel 1 -> NO.
    // Ej: requiredLevel 3 (Todos), userLevel 1 -> OK. userLevel 3 -> OK.
    return hasAccess;
  };

  const value = {
    accessConfig,
    loading,
    canAccess,
    getRequiredLevel,
    updateConfig,
    userLevel,
    isAdmin,
    refreshConfig: fetchAccessConfig,
  };

  // Log the full context state for debugging
  useEffect(() => {
    console.log("[AccessContext] Current state:", {
      userLevel,
      isAdmin,
      accessConfig,
      tutorial_gallery_level: accessConfig?.tutorial_gallery,
    });
  }, [userLevel, isAdmin, accessConfig]);

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
};
